import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { usePositions } from '../../services/api/queries/usePortfolio';
import { useUserSettings } from '../../services/api/queries/useUserSettings';
import { useUpdateCashBalance } from '../../services/api/queries/usePortfolioCash';
import type { UpdateCashRequest } from '../../services/api/cashApi';
import { showError, showSuccess } from '../../utils/alert';

/** The unlinked balance row every cash account is stored as. */
const CASH_SYMBOL = 'CASH';

/**
 * Currencies offered when opening a new cash account. The API accepts any
 * ISO-4217 code (``^[A-Z]{3}$``), so this is a convenience list; accounts the
 * user already holds are filtered out of it.
 */
const CURRENCY_OPTIONS = [
  'USD',
  'HKD',
  'CNY',
  'TWD',
  'JPY',
  'KRW',
  'SGD',
  'EUR',
  'GBP',
  'CHF',
  'AUD',
  'CAD',
];

/**
 * Group an amount's integer part with thousand separators while it is typed.
 *
 * A controlled input that rewrites its own value is only safe for the shapes it
 * understands: anything that is not (yet) a number — a lone "-", a trailing
 * "1.", a second "." — is passed through untouched so a half-finished entry is
 * never mangled under the user's cursor.
 */
export function formatAmountInput(raw: string): string {
  const cleaned = raw.replace(/[,\s]/g, '');
  const match = /^(-?)(\d*)(\.\d*)?$/.exec(cleaned);
  if (!match) return raw;
  const [, sign, whole, fraction = ''] = match;
  return `${sign}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction}`;
}

/** The number behind a draft, or null when it is not one (yet). */
export function parseAmountInput(text: string): number | null {
  const cleaned = text.replace(/[,\s]/g, '');
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/** ``-6,000 HKD`` — the code is spelled out, so no currency symbol can mislead. */
function formatAmount(value: number, currency: string, locale?: string): string {
  const amount = Math.abs(value).toLocaleString(locale, { maximumFractionDigits: 2 });
  return `${value < 0 ? '-' : ''}${amount} ${currency}`;
}

/** Percent string for a stored fraction, rounded so it cannot look dirty. */
function rateToPercent(rate: number | null | undefined): string {
  return rate === null || rate === undefined ? '' : String(Number((Number(rate) * 100).toFixed(4)));
}

interface CashAccount {
  currency: string;
  balance: number;
  rate: number | null;
}

interface RowError {
  /** The currency the message belongs to, or null for the new-account row. */
  currency: string | null;
  message: string;
}

export function AddCashScreen() {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { data: userSettings } = useUserSettings();
  const { data: positions = [], isLoading } = usePositions();
  const { mutateAsync } = useUpdateCashBalance();
  const locale = i18n.resolvedLanguage || undefined;

  // Drafts are keyed by currency and only exist once touched, so a refetch can
  // re-seed every field the user is not editing.
  const [amountDrafts, setAmountDrafts] = useState<Record<string, string>>({});
  const [rateDrafts, setRateDrafts] = useState<Record<string, string>>({});
  const [newAmount, setNewAmount] = useState('');
  const [newRate, setNewRate] = useState('');
  const [newCurrency, setNewCurrency] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [rowError, setRowError] = useState<RowError | null>(null);

  const accounts: CashAccount[] = useMemo(
    () =>
      positions
        .filter((position) => position.symbol?.trim().toUpperCase() === CASH_SYMBOL)
        .map((position) => ({
          currency: position.currency,
          balance: Number(position.value ?? position.quantity ?? 0) || 0,
          rate: position.cash_interest_rate ?? null,
        }))
        .sort((a, b) => a.currency.localeCompare(b.currency)),
    [positions]
  );

  const baseCurrency = userSettings?.base_currency ?? 'USD';
  const held = new Set(accounts.map((account) => account.currency));
  const offered = CURRENCY_OPTIONS.filter((code) => !held.has(code));
  const currencyOptions = offered.length > 0 ? offered : CURRENCY_OPTIONS;
  const draftCurrency =
    newCurrency ?? (currencyOptions.includes(baseCurrency) ? baseCurrency : currencyOptions[0]);

  const amountOf = (account: CashAccount) =>
    amountDrafts[account.currency] ?? formatAmountInput(String(account.balance));
  const rateOf = (account: CashAccount) =>
    rateDrafts[account.currency] ?? rateToPercent(account.rate);

  /** Translate a percent draft into the fraction the API stores, or null if blank. */
  const readRate = (draft: string): number | null | 'invalid' => {
    if (!draft.trim()) return null;
    const percent = parseFloat(draft);
    if (!Number.isFinite(percent) || percent < 0 || percent > 100) return 'invalid';
    return percent / 100;
  };

  /**
   * Send one currency's statement. ``saved`` is what the server last reported for
   * that account, so "unchanged" can be told from "clear it" and a submission
   * that would do nothing is refused rather than posted as a no-op.
   */
  const submit = async (
    currency: string,
    amountText: string,
    rateText: string,
    saved: { balance: number; rate: number | null }
  ): Promise<boolean> => {
    const statesBalance = amountText.trim() !== '';
    const balance = parseAmountInput(amountText);
    if (statesBalance && balance === null) {
      setRowError({
        currency,
        message: t('portfolio.cashAmountInvalid', { defaultValue: 'Enter a valid amount.' }),
      });
      return false;
    }
    // The field is prefilled with the stored balance, so "stated" is not the same
    // as "changed": only a real difference is sent, which keeps a rate-only edit
    // from restating the amount.
    const balanceChanged = statesBalance && balance !== saved.balance;

    const rateChanged = rateText.trim() !== rateToPercent(saved.rate);
    const nextRate = readRate(rateText);
    if (nextRate === 'invalid') {
      setRowError({
        currency,
        message: t('portfolio.cashInterestRateRange', {
          defaultValue: 'Must be between 0 and 100 (percent)',
        }),
      });
      return false;
    }

    if (!balanceChanged && !rateChanged) {
      setRowError({
        currency,
        message: t('portfolio.cashNothingToUpdate', {
          defaultValue: 'The balance and the margin rate are both unchanged.',
        }),
      });
      return false;
    }

    setRowError(null);
    setSaving(currency);
    try {
      const request: UpdateCashRequest = { currency };
      if (balanceChanged && balance !== null) request.balance = balance;
      // Sent explicitly (null when blank) so clearing a stale rate is possible;
      // omitted entirely when untouched, so a rate is never silently dropped.
      if (rateChanged) request.cash_interest_rate = nextRate;
      await mutateAsync(request);
      showSuccess(t('portfolio.cashBalanceUpdated', { defaultValue: 'Cash balance updated' }));
      setAmountDrafts((drafts) => dropKey(drafts, currency));
      setRateDrafts((drafts) => dropKey(drafts, currency));
      return true;
    } catch (mutationError: any) {
      showError(
        mutationError?.message ||
          t('portfolio.cashAddFailed', { defaultValue: 'Could not update the cash balance' })
      );
      return false;
    } finally {
      setSaving(null);
    }
  };

  const addAccount = async () => {
    if (!newAmount.trim() && !newRate.trim()) {
      // Nothing to open an account with: the API refuses a statement that says
      // neither, so the message names what is missing instead of "unchanged".
      setRowError({
        currency: null,
        message: t('portfolio.cashNothingToAdd', {
          defaultValue: 'Enter an amount, a margin rate, or both.',
        }),
      });
      return;
    }
    const ok = await submit(draftCurrency, newAmount, newRate, { balance: 0, rate: null });
    if (ok) {
      setNewAmount('');
      setNewRate('');
      setNewCurrency(null);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <Text style={styles.sectionTitle}>
          {t('portfolio.cashBalancesTitle', { defaultValue: 'Cash balances' })}
        </Text>

        {isLoading ? (
          <ActivityIndicator color={theme.colors.primary} />
        ) : accounts.length === 0 ? (
          <Text style={styles.helper}>
            {t('portfolio.cashNoAccounts', {
              defaultValue: 'No cash account yet — add one below.',
            })}
          </Text>
        ) : (
          <View style={styles.card}>
            <View style={styles.headerRow}>
              <View style={styles.currencyColumn} />
              <Text style={[styles.columnLabel, styles.amountColumn]}>
                {t('portfolio.cashAmountLabel', { defaultValue: 'Amount' })}
              </Text>
              <Text style={[styles.columnLabel, styles.rateColumn]}>
                {t('portfolio.cashRateLabel', { defaultValue: 'Margin %' })}
              </Text>
              <View style={styles.actionColumn} />
            </View>

            {accounts.map((account, index) => (
              <View key={account.currency}>
                {index > 0 && <View style={styles.divider} />}
                <View style={styles.accountRow}>
                  <Text style={styles.currency}>{account.currency}</Text>
                  <TextInput
                    style={[styles.control, styles.amountColumn, styles.amountInput]}
                    value={amountOf(account)}
                    onChangeText={(text) => {
                      setAmountDrafts((drafts) => ({
                        ...drafts,
                        [account.currency]: formatAmountInput(text),
                      }));
                      setRowError(null);
                    }}
                    placeholder={t('portfolio.cashBalancePlaceholder', {
                      defaultValue: 'e.g. -250000',
                    })}
                    placeholderTextColor={theme.colors.textTertiary}
                    keyboardType="numbers-and-punctuation"
                    accessibilityLabel={`${account.currency} cash balance`}
                    editable={saving === null}
                  />
                  <View style={[styles.control, styles.rateColumn, styles.rateField]}>
                    <TextInput
                      style={styles.rateInput}
                      value={rateOf(account)}
                      onChangeText={(text) => {
                        setRateDrafts((drafts) => ({ ...drafts, [account.currency]: text }));
                        setRowError(null);
                      }}
                      placeholder={t('portfolio.cashInterestRatePlaceholder', {
                        defaultValue: '6.5',
                      })}
                      placeholderTextColor={theme.colors.textTertiary}
                      keyboardType="decimal-pad"
                      accessibilityLabel={`${account.currency} margin rate`}
                      editable={saving === null}
                    />
                    <Text style={styles.rateSuffix}>%</Text>
                  </View>
                  <TouchableOpacity
                    style={[
                      styles.actionColumn,
                      styles.rowButton,
                      saving !== null && styles.disabled,
                    ]}
                    onPress={() =>
                      submit(account.currency, amountOf(account), rateOf(account), {
                        balance: account.balance,
                        rate: account.rate,
                      })
                    }
                    disabled={saving !== null}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    accessibilityLabel={`${t('portfolio.cashUpdate', { defaultValue: 'Update' })} ${account.currency}`}
                  >
                    {saving === account.currency ? (
                      <ActivityIndicator size="small" color={theme.colors.textInverse} />
                    ) : (
                      <Text style={styles.rowButtonText}>
                        {t('portfolio.cashUpdate', { defaultValue: 'Update' })}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>

                {/* Only a borrowed account has a carry to price, so the line is not
                    paid for by the balances that do not. */}
                {account.balance < 0 && account.rate !== null && (
                  <Text style={styles.rowHelper}>
                    {t('portfolio.cashInterestLine', {
                      rate: `${parseFloat(rateToPercent(account.rate)).toFixed(2)}%`,
                      amount: formatAmount(
                        Math.abs((account.balance * account.rate) / 12),
                        account.currency,
                        locale
                      ),
                      defaultValue: 'At {{rate}} p.a. — about {{amount}} per month.',
                    })}
                  </Text>
                )}

                {rowError?.currency === account.currency && (
                  <Text style={styles.error}>{rowError.message}</Text>
                )}
              </View>
            ))}
          </View>
        )}

        <Text style={styles.sectionTitle}>
          {t('portfolio.cashAddCurrencyTitle', { defaultValue: 'Add a currency' })}
        </Text>
        <View style={styles.addRow}>
          <TextInput
            style={[styles.control, styles.amountColumn, styles.amountInput]}
            value={newAmount}
            onChangeText={(text) => {
              setNewAmount(formatAmountInput(text));
              setRowError(null);
            }}
            placeholder={t('portfolio.cashBalancePlaceholder', { defaultValue: 'e.g. -250000' })}
            placeholderTextColor={theme.colors.textTertiary}
            keyboardType="numbers-and-punctuation"
            accessibilityLabel={t('portfolio.cashBalanceLabel', {
              defaultValue: 'Cash balance (negative = margin borrowing)',
            })}
            editable={saving === null}
          />
          <TouchableOpacity
            style={[styles.control, styles.pickerTrigger, saving !== null && styles.disabled]}
            onPress={() => setPickerOpen((open) => !open)}
            disabled={saving !== null}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={`${t('portfolio.cashCurrency', { defaultValue: 'Cash currency' })} ${draftCurrency}`}
          >
            <Text style={styles.pickerValue}>{draftCurrency}</Text>
            <Text style={styles.pickerCaret}>{pickerOpen ? '▲' : '▼'}</Text>
          </TouchableOpacity>
          <View style={[styles.control, styles.rateColumn, styles.rateField]}>
            <TextInput
              style={styles.rateInput}
              value={newRate}
              onChangeText={(text) => {
                setNewRate(text);
                setRowError(null);
              }}
              placeholder={t('portfolio.cashInterestRatePlaceholder', { defaultValue: '6.5' })}
              placeholderTextColor={theme.colors.textTertiary}
              keyboardType="decimal-pad"
              accessibilityLabel={`${draftCurrency} margin rate`}
              editable={saving === null}
            />
            <Text style={styles.rateSuffix}>%</Text>
          </View>
          <TouchableOpacity
            style={[styles.addButton, saving !== null && styles.disabled]}
            onPress={addAccount}
            disabled={saving !== null}
            activeOpacity={0.7}
            accessibilityRole="button"
            accessibilityLabel={t('portfolio.cashAddCurrencyTitle', {
              defaultValue: 'Add a currency',
            })}
          >
            {saving === draftCurrency ? (
              <ActivityIndicator size="small" color={theme.colors.textInverse} />
            ) : (
              <Text style={styles.rowButtonText}>
                {t('portfolio.cashAdd', { defaultValue: 'Add' })}
              </Text>
            )}
          </TouchableOpacity>
        </View>

        {pickerOpen && (
          <ScrollView style={styles.pickerList} nestedScrollEnabled>
            {currencyOptions.map((code) => (
              <TouchableOpacity
                key={code}
                style={styles.pickerOption}
                onPress={() => {
                  setNewCurrency(code);
                  setPickerOpen(false);
                  setRowError(null);
                }}
                activeOpacity={0.7}
              >
                <Text style={styles.pickerOptionText}>{code}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        )}

        {rowError?.currency === null && <Text style={styles.error}>{rowError.message}</Text>}

        <Text style={styles.helper}>
          {t('portfolio.cashInterestRateHelp', {
            defaultValue:
              'The margin rate is the borrow rate on a negative balance, shown for reference — not a deposit rate — and nothing is accrued into it.',
          })}
        </Text>
        <Text style={styles.helper}>
          {t('portfolio.cashHelp', {
            defaultValue:
              'Negative is margin borrowing; every change is kept as a deposit or withdrawal trade, so the balance stays auditable.',
          })}
        </Text>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

/** Drop one currency's draft so the freshly refetched balance seeds the field. */
function dropKey(drafts: Record<string, string>, key: string): Record<string, string> {
  const { [key]: _dropped, ...rest } = drafts;
  return rest;
}

/**
 * Shared metrics for every control in a row, so the amount box, the rate box and
 * the currency trigger are the same height and sit on the same baseline.
 */
function controlMetrics(theme: any) {
  return {
    minHeight: theme.spacing['4xl'],
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xxs,
    borderRadius: theme.radii.sm,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.backgroundElevated,
    color: theme.colors.text,
    fontSize: theme.typography.fontSizeSm,
  };
}

function createThemedStyles(theme: any) {
  const control = controlMetrics(theme);
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: theme.colors.background },
    content: { padding: theme.spacing.md },
    sectionTitle: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      marginBottom: theme.spacing.xs,
      marginTop: theme.spacing.sm,
    },
    card: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.radii.md,
      borderWidth: 1,
      borderColor: theme.colors.border,
      paddingHorizontal: theme.spacing.sm,
      paddingVertical: theme.spacing.xxs,
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    columnLabel: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      textAlign: 'right',
    },
    accountRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xxs,
    },
    currencyColumn: { width: theme.spacing['5xl'] },
    currency: {
      width: theme.spacing['5xl'],
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    // The amount takes whatever the fixed columns leave, and the rate column is
    // only as wide as the widest rate it can hold (three digits plus its %).
    amountColumn: { flex: 1 },
    rateColumn: { width: theme.spacing['5xl'] + theme.spacing.sm },
    rateField: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing.xxs,
    },
    rateInput: {
      flex: 1,
      minWidth: 0,
      padding: 0,
      textAlign: 'right',
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
    },
    rateSuffix: { color: theme.colors.textTertiary, fontSize: theme.typography.fontSizeXs },
    actionColumn: { width: theme.spacing['5xl'] + theme.spacing.md },
    amountInput: { textAlign: 'right' },
    control,
    divider: { height: 1, backgroundColor: theme.colors.divider },
    addRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
    },
    pickerTrigger: {
      width: theme.spacing['5xl'] + theme.spacing.lg,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    pickerValue: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    pickerCaret: { color: theme.colors.textSecondary, fontSize: theme.typography.fontSizeXs },
    pickerList: {
      maxHeight: theme.spacing['5xl'] * 4,
      marginTop: theme.spacing.xs,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: theme.radii.md,
      backgroundColor: theme.colors.surface,
    },
    pickerOption: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.divider,
    },
    pickerOptionText: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    rowButton: {
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: theme.spacing['4xl'],
      borderRadius: theme.radii.sm,
    },
    addButton: {
      width: theme.spacing['5xl'],
      minHeight: theme.spacing['4xl'],
      backgroundColor: theme.colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.radii.sm,
    },
    rowButtonText: {
      color: theme.colors.textInverse,
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
    },
    rowHelper: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      paddingBottom: theme.spacing.xxs,
      textAlign: 'right',
    },
    helper: {
      color: theme.colors.textTertiary,
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.sm,
    },
    error: {
      color: theme.colors.error,
      fontSize: theme.typography.fontSizeXs,
      paddingVertical: theme.spacing.xxs,
    },
    disabled: { opacity: 0.6 },
  });
}
